/**
 * Project Domain Schemas
 * Validation schemas for project entities and operations
 */

import { z } from 'zod';
import { uuidSchema, dateStringSchema, moneySchema, colorHexSchema } from './common.schemas';

/**
 * Project Status Enum
 */
export const projectStatusSchema = z.enum(['planning', 'active', 'on_hold', 'completed', 'cancelled']);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/**
 * Project Priority Enum
 */
export const projectPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type ProjectPriority = z.infer<typeof projectPrioritySchema>;

/**
 * Base Project Schema
 * Represents a complete project record from the database
 */
export const projectSchema = z.object({
  id: uuidSchema,
  user_id: uuidSchema,
  organization_id: uuidSchema.nullable(),
  client_id: uuidSchema.nullable(),
  name: z.string().min(1).max(200),
  description: z.string().max(5000).nullable(),
  status: projectStatusSchema.default('planning'),
  priority: projectPrioritySchema.default('medium'),
  start_date: dateStringSchema.nullable(),
  end_date: dateStringSchema.nullable(),
  deadline: dateStringSchema.nullable(),
  budget: moneySchema.nullable(),
  actual_cost: moneySchema.nullable(),
  color: colorHexSchema.nullable(),
  tags: z.array(z.string()).default([]),
  manager_id: uuidSchema.nullable(),
  team_members: z.array(uuidSchema).default([]),
  custom_fields: z.record(z.unknown()).default({}),
  progress_percentage: z.number().min(0).max(100).default(0),
  is_archived: z.boolean().default(false),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type Project = z.infer<typeof projectSchema>;

/**
 * Create Project Schema
 * For creating new projects
 */
export const createProjectSchema = projectSchema.omit({
  id: true,
  actual_cost: true,
  progress_percentage: true,
  is_archived: true,
  created_at: true,
  updated_at: true,
}).extend({
  user_id: uuidSchema.optional(),
  organization_id: uuidSchema.optional(),
  name: z.string().min(1, 'Project name is required').max(200, 'Project name must be less than 200 characters').trim(),
  description: z.string().max(5000).optional().nullable().transform(val => val || null),
  start_date: z.string().optional().nullable().transform(val => val || null),
  end_date: z.string().optional().nullable().transform(val => val || null),
  deadline: z.string().optional().nullable().transform(val => val || null),
  budget: z.coerce.number().nonnegative().optional().nullable().transform(val => val || null),
}).refine(
  data => {
    if (data.start_date && data.end_date) {
      return new Date(data.end_date) >= new Date(data.start_date);
    }
    return true;
  },
  {
    message: 'End date must be on or after start date',
    path: ['end_date'],
  }
).refine(
  data => {
    if (data.start_date && data.deadline) {
      return new Date(data.deadline) >= new Date(data.start_date);
    }
    return true;
  },
  {
    message: 'Deadline must be on or after start date',
    path: ['deadline'],
  }
);

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/**
 * Update Project Schema
 * For partial updates
 */
export const updateProjectSchema = projectSchema.partial().required({
  id: true,
}).omit({
  user_id: true,
  created_at: true,
});

export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

/**
 * Project Filter Schema
 * For querying projects
 */
export const projectFilterSchema = z.object({
  status: z.array(projectStatusSchema).optional(),
  priority: z.array(projectPrioritySchema).optional(),
  client_id: uuidSchema.optional(),
  organization_id: uuidSchema.optional(),
  manager_id: uuidSchema.optional(),
  team_member_id: uuidSchema.optional(),
  tags: z.array(z.string()).optional(),
  search: z.string().max(500).optional(),
  start_date_after: dateStringSchema.optional(),
  start_date_before: dateStringSchema.optional(),
  deadline_before: dateStringSchema.optional(),
  is_archived: z.boolean().optional(),
  has_budget: z.boolean().optional(),
});

export type ProjectFilter = z.infer<typeof projectFilterSchema>;

/**
 * Project Milestone Schema
 */
export const projectMilestoneSchema = z.object({
  id: uuidSchema,
  project_id: uuidSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable(),
  due_date: dateStringSchema,
  completed_at: dateStringSchema.nullable(),
  is_completed: z.boolean().default(false),
  order_index: z.number().int().nonnegative().default(0),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type ProjectMilestone = z.infer<typeof projectMilestoneSchema>;

/**
 * Create Project Milestone Schema
 */
export const createProjectMilestoneSchema = projectMilestoneSchema.omit({
  id: true,
  completed_at: true,
  is_completed: true,
  created_at: true,
  updated_at: true,
}).extend({
  name: z.string().min(1, 'Milestone name is required').max(200, 'Milestone name must be less than 200 characters').trim(),
  due_date: z.string(), // Required for milestones
  description: z.string().max(2000).optional().nullable().transform(val => val || null),
});

export type CreateProjectMilestoneInput = z.infer<typeof createProjectMilestoneSchema>;

/**
 * Project Time Entry Schema
 */
export const projectTimeEntrySchema = z.object({
  id: uuidSchema,
  project_id: uuidSchema,
  user_id: uuidSchema,
  task_id: uuidSchema.nullable(),
  description: z.string().max(1000).nullable(),
  duration_minutes: z.number().int().positive(),
  start_time: dateStringSchema,
  end_time: dateStringSchema.nullable(),
  billable: z.boolean().default(true),
  hourly_rate: moneySchema.nullable(),
  created_at: dateStringSchema,
  updated_at: dateStringSchema,
});

export type ProjectTimeEntry = z.infer<typeof projectTimeEntrySchema>;

/**
 * Create Project Time Entry Schema
 */
export const createProjectTimeEntrySchema = projectTimeEntrySchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  user_id: uuidSchema.optional(),
  description: z.string().max(1000).optional().nullable().transform(val => val || null),
});

export type CreateProjectTimeEntryInput = z.infer<typeof createProjectTimeEntrySchema>;
